/**
 * Shared sanitization utilities for error handling, logging, and Sentry
 * Centralizes sensitive data redaction logic across the application
 */

const SENSITIVE_FIELDS = ["authorization", "cookie", "password", "token", "key", "secret"];

/**
 * Redacts sensitive data within an object by replacing values of sensitive fields with "[REDACTED]".
 *
 * **When to use**: For simple objects where you only need to hide sensitive fields and
 * you're certain the data doesn't contain BigInt, circular references, or Date objects.
 *
 * **When NOT to use**: For untrusted/complex data (e.g., BullMQ job payloads, user input,
 * blockchain data). Use {@link serializeArgument} instead, which handles edge cases safely.
 *
 * @param obj - The object to redact sensitive fields from
 * @returns A new object with sensitive fields redacted (does not mutate original)
 */
export function redactSensitiveData(obj: unknown): unknown {
  if (typeof obj !== "object" || obj === null) return obj;

  // clone the object before modifying to avoid mutating the original object
  const objClone = Array.isArray(obj) ? [...obj] : { ...obj };

  for (const key in objClone) {
    const clone = objClone as Record<string, unknown>;
    if (SENSITIVE_FIELDS.some((field) => key.toLowerCase().includes(field.toLowerCase()))) {
      clone[key] = "[REDACTED]";
    } else if (typeof clone[key] === "object") {
      clone[key] = redactSensitiveData(clone[key]);
    }
  }

  return objClone;
}

/**
 * Safely serialize any value for logging, Sentry, or external transmission.
 *
 * **Handles edge cases that would crash JSON.stringify:**
 * - BigInt → converted to string
 * - Circular references → replaced with "[Circular]"
 * - Date objects → converted to ISO string
 * - Large arrays → truncated to 10 items with count indicator
 * - Serialization errors → returns "[Serialization Error: ...]" instead of throwing
 *
 * **Also redacts sensitive data** by calling {@link redactSensitiveData} internally.
 *
 * **When to use**: For untrusted/complex data like BullMQ job payloads, user input,
 * API responses, blockchain data, or any data that might contain BigInt or circular refs.
 *
 * @param arg - Any value to serialize safely
 * @returns A JSON-safe representation of the value with sensitive data redacted
 *
 * @example
 * // Safe for job data that may contain BigInt
 * logger.error("Job failed", { jobData: serializeArgument(job.data) });
 *
 * // Safe for Sentry extras
 * Sentry.setExtra("payload", serializeArgument(complexObject));
 */
export function serializeArgument(arg: unknown): unknown {
  try {
    if (arg === null || arg === undefined) return arg;
    if (typeof arg === "bigint") return arg.toString();
    if (arg instanceof Date) return arg.toISOString();

    // Handle arrays
    if (Array.isArray(arg)) {
      // Limit array size to prevent huge contexts
      const limited = arg.slice(0, 10);
      const serialized = limited.map((item) => serializeArgument(item));
      if (arg.length > 10) {
        serialized.push(`... and ${arg.length - 10} more items`);
      }
      return serialized;
    }

    // Handle objects
    if (typeof arg === "object") {
      const seen = new WeakSet();
      const serialized = JSON.parse(
        JSON.stringify(arg, (_key, value) => {
          // Handle circular references
          if (typeof value === "object" && value !== null) {
            if (seen.has(value)) return "[Circular]";
            seen.add(value);
          }
          // Handle BigInt in nested objects
          if (typeof value === "bigint") return value.toString();
          return value;
        }),
      );

      // Sanitize the result
      const redacted = redactSensitiveData(serialized);
      return redacted;
    }

    return arg;
  } catch (error) {
    return `[Serialization Error: ${error instanceof Error ? error.message : String(error)}]`;
  }
}
